import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays } from 'date-fns';

function TeamDashboard() {
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => {
    fetchTeamStats();
  }, [dateRange]);

  const fetchTeamStats = async () => {
    setLoading(true);
    try {
      const endDate = format(new Date(), 'yyyy-MM-dd');
      const startDate = format(subDays(new Date(), parseInt(dateRange)), 'yyyy-MM-dd');

      const response = await axios.get(`${API}/dashboard/team-stats`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setTeamStats(response.data);
    } catch (error) {
      console.error('Error fetching team stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-10 h-10 border-4 border-slate-200 dark:border-slate-700 border-t-slate-600 rounded-full"></div>
      </div>
    );
  }

  const chartData = teamStats?.by_user.map(user => ({
    name: user.name.split(' ')[0],
    calls: user.total_calls,
    emails: user.total_emails,
    meetings: user.total_meetings,
    deals: user.total_deals,
  })) || [];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 -m-4 lg:-m-6 p-4 lg:p-6 space-y-5" data-testid="team-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Team Performance</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">Monitor your team's activities and results</p>
        </div>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value)}
          className="px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 bg-white dark:bg-slate-800"
        >
          <option value="7">Last 7 Days</option>
          <option value="30">Last 30 Days</option>
          <option value="90">Last 90 Days</option>
        </select>
      </div>

      {/* Overall Team Stats - Clean style with BLACK values */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Calls</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{teamStats?.overall?.total_calls || 0}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Total Emails</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{teamStats?.overall?.total_emails || 0}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Meetings</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{teamStats?.overall?.total_meetings || 0}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Demos</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{teamStats?.overall?.total_demos || 0}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Qualified Leads</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{teamStats?.overall?.total_qualified_leads || 0}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Deals Closed</p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{teamStats?.overall?.total_deals || 0}</p>
        </div>
      </div>

      {/* Team Comparison Chart */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-6">Team Member Comparison</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="name" stroke="#64748B" />
            <YAxis stroke="#64748B" />
            <Tooltip
              contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
            />
            <Legend />
            <Bar dataKey="calls" fill="#334155" name="Calls" radius={[4, 4, 0, 0]} />
            <Bar dataKey="emails" fill="#64748B" name="Emails" radius={[4, 4, 0, 0]} />
            <Bar dataKey="meetings" fill="#94A3B8" name="Meetings" radius={[4, 4, 0, 0]} />
            <Bar dataKey="deals" fill="#475569" name="Deals" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Individual Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamStats?.by_user.map((user, idx) => (
          <div key={idx} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center mb-4">
              <div className="h-12 w-12 rounded-full bg-slate-900 flex items-center justify-center text-white font-semibold text-lg">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <div className="ml-3">
                <h4 className="font-semibold text-slate-900 dark:text-white">{user.name}</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400 capitalize">{user.role.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Calls:</span>
                <span className="font-semibold text-slate-900 dark:text-white">{user.total_calls}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Emails:</span>
                <span className="font-semibold text-slate-900 dark:text-white">{user.total_emails}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Meetings:</span>
                <span className="font-semibold text-slate-900 dark:text-white">{user.total_meetings}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Demos:</span>
                <span className="font-semibold text-slate-900 dark:text-white">{user.total_demos}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">Qualified Leads:</span>
                <span className="font-semibold text-slate-900 dark:text-white">{user.total_qualified_leads}</span>
              </div>
              <div className="flex justify-between text-sm border-t border-slate-100 dark:border-slate-700 pt-2 mt-2">
                <span className="text-slate-600 dark:text-slate-400 font-medium">Deals Closed:</span>
                <span className="font-bold text-slate-900 dark:text-white">{user.total_deals}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TeamDashboard;